import {
  BUTTON_FACTORY_BUSY,
  INITIALIZE_WIZARD,
  WIZARD_CONTINUE,
  WIZARD_BACK,
  WIZARD_SET_CURRENT_PAGE,
  CREATE_BUTTON_SUCCESS,
  CREATE_BUTTON_ERROR,
  EDIT_BUTTON_SUCCESS,
  EDIT_BUTTON_ERROR,
  GET_BUTTON_DATA_SUCCESS,
  GET_BUTTON_DATA_ERROR,
  UPDATE_CODE_SNIPPET,
  SHOW_LOGIN_MODAL,
  HIDE_LOGIN_MODAL,
  SHOW_ACTIONABLE_ERROR_MODAL,
  HIDE_ACTIONABLE_ERROR_MODAL,
  SHOW_UNACTIONABLE_ERROR_MODAL,
  HIDE_UNACTIONABLE_ERROR_MODAL,
  SHOW_LEAVE_MODAL,
  HIDE_LEAVE_MODAL,
  SET_ERROR_LOADING_IMAGE,
  RESET_ERROR_LOADING_IMAGE,
} from '../actions/actionsButtonFactory';
import {
  getNextStep,
  getOptionsForLanguageField,
  getPreviousStep,
  WIZARD_TOTAL_STEPS,
} from '../utils/buttonFactoryUtils';
import { transformServerFormatToClient } from '../lib/buttonModel';
import { getServerProps } from '../utils/serverProps';
import _get from 'lodash/get';

const settingsLogoUrl = getSettingsLogoUrl();

const getDefaultValueForLanguage = () => {
  const serverProps = getServerProps();
  const countryAndLanguageDetails = _get(
    serverProps,
    'countryAndLanguageDetails'
  );
  const selectedCountry = _get(serverProps, 'selectedCountry');
  const languages = getOptionsForLanguageField(
    countryAndLanguageDetails,
    selectedCountry
  );
  // Get first language
  let language = _get(languages, '[0].value');
  return language || 'en';
};

export const LOGO_TARGET_BUTTON = 'BUTTON';
export const LOGO_TARGET_PROFILE = 'PROFILE';
export const LOGO_TARGET_NOLOGO = 'NOLOGO';

export const defaultButton = {
  accountIdentifierType: 'merchantAccountId',
  allowPrograms: 'No',
  amountType: 'variable',
  amountValue: '',
  business: getServerProps().accountIdentifier.email,
  buttonSize: 'large',
  countryCode: getServerProps().country,
  coverFeesEnabled: 'yes',
  extraVariables: {},
  extraVariablesView: '',
  itemNumber: '',
  language: getDefaultValueForLanguage(),
  listPrograms: 'no',
  logoTarget: settingsLogoUrl ? LOGO_TARGET_PROFILE : LOGO_TARGET_NOLOGO,
  logoUrl: '',
  merchantAmount1: '',
  merchantAmount2: '',
  merchantAmount3: '',
  noNote: 'no',
  noShipping: 'yes',
  organizationName: getServerProps().organizationName,
  programNames: [],
  purpose: '',
  receiverCurrency: getServerProps().primaryCurrency[0],
  recurringEnabled: 'yes',
  settingsLogoUrl,
  showAcceptedCardLogos: false,
  uploadNewLogo: 'no',
  usePaypalButtonStyle: 'yes',
  version: '2.0', // just to differntiate the experience where button was created
};

export const initialState = {
  formattedButton: defaultButton,
};

const defaultState = {
  currentStep: 1,
  totalSteps: WIZARD_TOTAL_STEPS,
  busy: true, // We fetch button on load, hence set this to busy
  customVariable: '<!DOCTYPE html>',
  waiting: false,
  showLoginModal: false,
  errorLoadingLogoImage: false,
  showUnactionableErrorModal: false,
  showActionableErrorModal: false,
  showLeaveModal: false,
};

function getFormattedButton(payload) {
  // organizationName is not stored in BF service
  // fetching from server to make sure it gets pre-filled when user is editing/duplicating a button
  return Object.assign({}, transformServerFormatToClient(payload), {
    organizationName: getServerProps().organizationName,
    language: payload.language,
  });
}
function getSettingsLogoUrl() {
  const merchantExperienceUrl = _get(
    getServerProps(),
    'merchantExperienceUrls.logoUrl'
  );
  const businessImageUrl = _get(getServerProps(), 'businessImageUrls.url');

  return merchantExperienceUrl ? merchantExperienceUrl : businessImageUrl;
}

export default function buttonFactory(state = defaultState, action) {
  switch (action.type) {
    case BUTTON_FACTORY_BUSY: {
      return {
        ...state,
        busy: true,
      };
    }

    case INITIALIZE_WIZARD: {
      const button = action.payload;
      let clientSideState = initialState;
      if (button) {
        clientSideState = {
          formattedButton: getFormattedButton(button),
        };
      }

      return {
        ...state,
        button: action.payload,
        ...clientSideState,
        busy: false,
      };
    }

    case WIZARD_CONTINUE: {
      const { currentStep } = state;

      return {
        ...state,
        currentStep: getNextStep(currentStep),
      };
    }

    case WIZARD_BACK: {
      const { currentStep } = state;

      return {
        ...state,
        currentStep: getPreviousStep(currentStep),
      };
    }

    case WIZARD_SET_CURRENT_PAGE: {
      return {
        ...state,
        currentStep: action.payload,
      };
    }

    case CREATE_BUTTON_SUCCESS: {
      return {
        ...state,
        button: action.payload,
        formattedButton: getFormattedButton(action.payload),
        busy: false,
        error: null,
        isButtonCreated: true,
      };
    }

    case CREATE_BUTTON_ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        error,
        busy: false,
      };
    }

    case EDIT_BUTTON_SUCCESS: {
      return {
        ...state,
        button: action.payload,
        formattedButton: getFormattedButton(action.payload),
        busy: false,
        error: null,
      };
    }

    case EDIT_BUTTON_ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        error,
        busy: false,
      };
    }

    case GET_BUTTON_DATA_SUCCESS: {
      const newState = {
        ...state,
        button: action.payload,
        formattedButton: getFormattedButton(action.payload),
        busy: false,
      };
      return newState;
    }

    case GET_BUTTON_DATA_ERROR: {
      const { error } = action.payload;

      return {
        ...state,
        error,
        busy: false,
      };
    }

    case UPDATE_CODE_SNIPPET: {
      return {
        ...state,
        customVariable: action.payload,
      };
    }

    case SHOW_LOGIN_MODAL: {
      return {
        ...state,
        showLoginModal: true,
      };
    }

    case HIDE_LOGIN_MODAL: {
      return {
        ...state,
        showLoginModal: false,
      };
    }

    case SHOW_ACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showActionableErrorModal: true,
      };
    }

    case HIDE_ACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showActionableErrorModal: false,
      };
    }

    case SHOW_UNACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showUnactionableErrorModal: true,
      };
    }

    case HIDE_UNACTIONABLE_ERROR_MODAL: {
      return {
        ...state,
        showUnactionableErrorModal: false,
      };
    }

    case SHOW_LEAVE_MODAL: {
      return {
        ...state,
        showLeaveModal: true,
      };
    }

    case HIDE_LEAVE_MODAL: {
      return {
        ...state,
        showLeaveModal: false,
      };
    }

    case SET_ERROR_LOADING_IMAGE: {
      return {
        ...state,
        errorLoadingLogoImage: true,
      };
    }

    case RESET_ERROR_LOADING_IMAGE: {
      return {
        ...state,
        errorLoadingLogoImage: false,
      };
    }

    default:
      return state;
  }
}
